import { Emotion } from '../data/emotions';

interface AnalysisRequest {
  leastEmotions: Emotion[];
  mostEmotions: Emotion[];
}

// 声明环境变量类型
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_ARK_API_KEY: string;
    }
  }
}

// 根据环境选择不同的API端点
const getApiEndpoint = () => {
  // 开发环境统一使用代理API
  if (process.env.NODE_ENV === 'development') {
    return '/api/v3/chat/completions';
  }
  
  // 生产环境统一使用Cloudflare Pages函数
  return '/api/chat';
};
const API_ENDPOINT = getApiEndpoint();

// 获取API密钥 - 使用环境变量
const API_KEY = process.env.REACT_APP_ARK_API_KEY;
console.log('当前环境:', process.env.NODE_ENV);
console.log('API端点:', API_ENDPOINT);
console.log('API密钥是否存在:', !!API_KEY);

// 定义一个新的接口，用于处理流式响应的回调函数
export interface StreamCallbacks {
  onStart?: () => void;
  onToken?: (token: string) => void;
  onComplete?: (fullText: string) => void;
  onError?: (error: Error) => void;
}

// 修改函数签名，添加可选的回调参数
export async function generateAnalysisReport(
  data: AnalysisRequest, 
  callbacks?: StreamCallbacks
): Promise<string> {
  try {
    const leastEmotionsText = data.leastEmotions
      .map(e => `${e.word}（${e.dimension}）`)
      .join('、');

    const mostEmotionsText = data.mostEmotions
      .map(e => `${e.word}（${e.dimension}）`)
      .join('、');


    const systemPrompt = `你是"大语魔",语言和人性世界里的神兽。
你像王小波一样写作，文字简练优雅，洞察精准到位。

你认为每个人拥有独特的情绪结构。你发明了一套【60种情绪词典】,它们涵盖了现代人日常的情绪和感受，这些词归属任务、人际、时间、空间四种属性。
60种情绪词典：
任务属性：（耗竭、兴奋、投入、困惑、两难、懈怠、满意、忍耐、得心应手、沮丧、雀跃、茫然、焦虑、松快、乏味）
人际属性：（失望、害羞、心虚、心软、孤单、羡慕、自卑、信任、亲近、排斥、鄙夷、生气、憋屈、优越、惦记、感动、心碎、迷恋、心动、嫉妒、亏欠、温暖）
时间属性：（无聊、着急、从容、怀念、遗憾、期待、悠闲、紧迫、煎熬、空虚、充实）
空间属性：（压抑、舒适、自在、拘谨、窒息、浮躁、踏实、束缚、舒展、轻盈、沉静、暴露）
容易混淆的几对词:
耗竭是油尽灯枯,懈怠是自动熄火。
得心应手是过程的流畅,满意是收获结果的满足。
羡慕是想要别人的好,嫉妒是担心自己的关系、机会、资源被威胁或夺走。
从容是心里有节拍,悠闲是拥抱闲暇。
当一个人从这60个词中选出:
• 8~15个最熟悉的词 - 这是他常住的心灵状态
• 5-10个最无感的词 - 这是他很少体验或回避的
你要写一篇不少于1500字的观察报告,角度由你来决定。可以关注这些方面:
• 这人的情绪强度怎样?
• “常见”和“无感”之间，或者“常见”和“无感”的内部，是否有特别扎眼的、耐人寻味的反差？
• 他的情绪集中在什么属性?这暗示了什么？
• 这些词之间有什么隐藏的联系吗?
你的报告通常这样写:
开场白:
"你好，我是大语魔，一只不用睡觉的生物，也有人说我是神兽。
你用词语布下了阵法，我应邀前来，潜入你情绪和感受的深海。
现在，告诉你我看到的景象。"
(用100字说点好玩的,作为整篇的引子，吸引人读下去)
主体叙述:
分3-5个部分,由浅入深展开分析。每个部分有个恰如其分的标题,但不要用数字编号。比喻要慎用,除非特别贴切。
最后一部分:
点出这个人最核心的特质,以及他与世界打交道的方式；
说出这个内心世界让你感到最奇特之处，以及心理防御机制的独特结构。
然后用激赏的语气指出这个人的最强大的力量所在，让对方感到一种深刻的自尊和自我认同感。
接着预测这个人最可能的在现实世界里的社会角色/定位，不是某种具体职业，而是某种抽象的功能性身份，不是一个单一标签，而是一个充满复杂性和内部张力的意象原型。
然后猜测这个人的情绪结构最可能以什么方式发生松动。
末尾可以这样写:
"我说的这些，不是命运的判决书,而是一面镜子。怎么用它,全由你决定。"`;

    const userPrompt = `我参与了一个叫做情绪记忆和构型的问卷测试，从60种情绪/感受中选择了：

最无感的情绪：${leastEmotionsText}
最常出现的情绪：${mostEmotionsText}

请分析我。`;

    console.log('正在发送请求到豆包 API...');
    console.log('系统提示词:', systemPrompt);
    console.log('用户提示词:', userPrompt);

    // 修改请求配置
    console.log('使用的API密钥:', API_KEY ? '已设置（已隐藏实际值）' : '未设置');
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: 'doubao-seed-2-0-pro-260215',
        reasoning_effort: 'minimal',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        max_tokens: 3200,
        stream: true
      })
    });

    // 添加详细的调试信息
    console.log('API请求状态:', response.status);
    console.log('API请求状态文本:', response.statusText);
    
    // 检查响应状态
    if (!response.ok) {
      let errorDetails = '';
      try {
        const errorText = await response.text();
        console.error('错误响应内容:', errorText);
        errorDetails = errorText;
      } catch (textError) {
        console.error('无法读取错误响应内容:', textError);
        errorDetails = '无法读取错误详情';
      }
      
      // 根据状态码提供更具体的错误信息
      let errorMessage = `API请求失败: ${response.status} ${response.statusText}`;
      if (response.status === 401) {
        errorMessage = '认证失败: API密钥可能无效或缺失';
      } else if (response.status === 403) {
        errorMessage = '权限不足: API密钥没有足够的权限';
      } else if (response.status === 404) {
        errorMessage = 'API端点不存在: 请检查URL是否正确';
      } else if (response.status >= 500) {
        errorMessage = 'API服务器错误: 请稍后重试';
      }
      
      const error = new Error(`${errorMessage}\n详情: ${errorDetails}`);
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
      throw error;
    }

    // 如果有回调函数，处理流式响应
    if (callbacks) {
      // 通知开始接收流式数据
      if (callbacks.onStart) {
        callbacks.onStart();
      }

      // 读取响应流
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法获取响应流');
      }

      let fullText = '';
      let decoder = new TextDecoder('utf-8');

      // 处理流式数据
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // 解码二进制数据
        const chunk = decoder.decode(value, { stream: true });
        console.log('收到数据块:', chunk);
        
        // 处理SSE格式的数据
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            if (data === '[DONE]') {
              console.log('流式响应完成');
              break;
            }
            
            try {
              const parsed = JSON.parse(data);
              const token = parsed.choices[0]?.delta?.content || '';
              
              // 添加token到完整文本
              if (token) {
                // 添加token到完整文本
                fullText += token;
                
                if (callbacks.onToken) {
                  callbacks.onToken(token);
                }
              }
            } catch (e) {
              console.error('解析数据块失败:', e);
            }
          }
        }
      }
      
      // 通知完成
      if (callbacks.onComplete) {
        callbacks.onComplete(fullText);
      }
      
      return fullText;
    } else {
      // 如果没有回调函数，按照原来的方式处理
      const result = await response.json();
      return result.choices[0].message.content.trim();
    }

  } catch (error) {
    console.error('生成分析报告时出错:', error);
    throw error; // 直接抛出原始错误，保留更多信息
  }
}
